import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HttpClient } from '@angular/common/http';
import { formatDisplayText } from '../../utils/text-case.utils';
import { Router } from '@angular/router';
import { executeNavigationFromConfig } from '../../shared-services/chart-wrapper.service';
import { ConfigService } from '../../shared-services/config.service';
import { saveAs } from 'file-saver';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-button',
  standalone: true,
  imports: [MatButtonModule, CommonModule, MatIconModule, MatTooltipModule],
  templateUrl: './button.component.html',
  styleUrl: './button.component.scss'
})
export class ButtonComponent implements OnInit, OnDestroy {
  @Input() data!: any;
  @Input() selectedKey!: string;
  @Input() showResetButton = false;
  @Output() selectionChange = new EventEmitter<string>();
  @Output() resetClick = new EventEmitter<void>();
  tooltipText = 'Insights';
  /** Whether this button is currently the active member of its `toggleGroup` (red vs gray). */
  toggleActive = false;
  private visibilityMatches = true;
  private readonly defaultInsightUrl = 'assets/tooltip-hover/tooltip-hover.json';
  private toggleGroupSub?: Subscription;
  private visibilitySub?: Subscription;

  constructor(
    private globalFilterService: GlobalFilterService,
    private http: HttpClient,
    private router: Router,
    private configService: ConfigService,
  ) {}

  get showInsightButton(): boolean {
    if (this.data?.key !== 'insights') {
      return false;
    }
    return this.data?.enableInsight !== false;
  }

  get isResetVisible(): boolean {
    if (this.data?.showReset === true) return true;
    if (this.data?.showReset === false) return false;
    return this.showResetButton;
  }

  get isInsightVisible(): boolean {
    if (this.data?.showInsight === true) return true;
    if (this.data?.showInsight === false) return false;
    return this.showInsightButton;
  }

  get showMainButton(): boolean {
    return this.data?.iconOnly !== true && this.visibilityMatches;
  }

  get isDisabled(): boolean {
    const widgetId = this.data?.requireReadyWidget;
    return !!widgetId && !this.globalFilterService.isWidgetReady(widgetId);
  }

  /** `toggleGroup`: config key shared by mutually-exclusive buttons (e.g. Column/Saved Selection). */
  get toggleGroup(): string {
    return String(this.data?.toggleGroup || '').trim();
  }

  get hasToggleGroup(): boolean {
    return !!this.toggleGroup;
  }

  /**
   * Per UI Standards (Point 3): button label in Capital Case unless overridden.
   */
  get displayLabel(): string {
    const mode = (this.data?.textCase || 'capital') as any;
    return formatDisplayText(this.data?.label, mode);
  }

  get insightTooltipText(): string {
    return this.tooltipText;
  }

  ngOnInit(): void {
    this.initToggleGroup();
    this.initFilterVisibility();

    if (!this.showInsightButton) {
      return;
    }

    if (this.data?.insightTooltip) {
      this.tooltipText = this.data.insightTooltip;
      return;
    }

    const insightApiUrl =
      typeof this.data?.insightApiUrl === 'string' && this.data.insightApiUrl.trim().length > 0
        ? this.data.insightApiUrl
        : this.defaultInsightUrl;
    this.http.get<any>(insightApiUrl).subscribe({
      next: (response) => {
        this.tooltipText = this.buildTooltipText(response);
      },
      error: () => {
        this.tooltipText = 'Insights';
      },
    });
  }

  ngOnDestroy(): void {
    this.toggleGroupSub?.unsubscribe();
    this.visibilitySub?.unsubscribe();
  }

  /**
   * Buttons sharing a `toggleGroup` behave as mutually-exclusive toggles (red = active,
   * gray = inactive) regardless of which page renders them. State lives in
   * GlobalFilterService under the group key so it survives independently of each
   * button's own click-driven filter key (used elsewhere, e.g. to open/close panels).
   */
  private initToggleGroup(): void {
    const toggleGroup = this.toggleGroup;
    if (!toggleGroup) {
      return;
    }

    const current = this.globalFilterService.getCurrentFilters();
    const currentActive =
      current?.requestParams?.[toggleGroup]?.[0] ?? current?.queryParams?.[toggleGroup]?.[0];

    if (currentActive !== undefined) {
      this.toggleActive = currentActive === this.data?.key;
    } else if (this.data?.toggleGroupDefault === true) {
      this.toggleActive = true;
      this.globalFilterService.updateFilterSelection(
        toggleGroup,
        [this.data.key],
        false,
        undefined,
        false,
      );
    }

    this.toggleGroupSub = this.globalFilterService.filter$.subscribe((filters) => {
      const isGlobalClear =
        !filters?.changedFilterKey &&
        this.isEmptyBag(filters?.queryParams) &&
        this.isEmptyBag(filters?.requestParams);

      if (isGlobalClear) {
        this.toggleActive = this.data?.toggleGroupDefault === true;
        if (this.toggleActive) {
          this.globalFilterService.updateFilterSelection(
            toggleGroup,
            [this.data.key],
            false,
            undefined,
            false,
          );
        }
        return;
      }

      const activeKey =
        filters?.requestParams?.[toggleGroup]?.[0] ?? filters?.queryParams?.[toggleGroup]?.[0];
      if (activeKey !== undefined) {
        this.toggleActive = activeKey === this.data?.key;
      }
    });
  }

  private isEmptyBag(bag: any): boolean {
    return !bag || Object.keys(bag).length === 0;
  }

  onClick(value: string) {
    if (!value || this.isDisabled) {
      return;
    }

    this.selectionChange.emit(value); // Notify parent

    if (this.data?.clearAllFilters === true || this.data?.key === 'reset') {
      this.globalFilterService.clearAllFilters();
      return;
    }

    // Insights button is informational only and should not mutate global filters.
    if (this.data?.key === 'insights') {
      return;
    }

    const filterKey = this.data?.filterKey || this.data?.key || this.data?.type;
    if (this.data && filterKey) {
      const toggleGroup = this.toggleGroup;
      const keys = toggleGroup && toggleGroup !== filterKey ? [filterKey, toggleGroup] : filterKey;
      this.globalFilterService.updateFilterSelection(
        keys,
        [value],
        this.data.isQueryParam
      );
    }

    this.executeConfiguredApiAction();

    executeNavigationFromConfig(this.router, this.data, { name: value, value });
  }

  /** Optional config-driven visibility tied to one global-filter value. */
  private initFilterVisibility(): void {
    const condition = this.data?.visibleWhenFilter;
    if (!condition?.key) {
      this.visibilityMatches = true;
      return;
    }

    this.syncFilterVisibility(this.globalFilterService.getCurrentFilters());
    this.visibilitySub = this.globalFilterService.filter$.subscribe((filters) =>
      this.syncFilterVisibility(filters)
    );
  }

  private syncFilterVisibility(filters: any): void {
    this.visibilityMatches = this.matchesFilterCondition(this.data?.visibleWhenFilter, filters);
  }

  private matchesFilterCondition(condition: any, filters: any): boolean {
    const key = String(condition?.key || '').trim();
    if (!key) return true;

    const actual = filters?.requestParams?.[key] ?? filters?.queryParams?.[key];
    const actualValues = (Array.isArray(actual) ? actual : [actual]).map((value) => String(value));
    const configured = condition?.values ?? condition?.value;
    const expectedValues = (Array.isArray(configured) ? configured : [configured])
      .filter((value) => value !== null && value !== undefined)
      .map((value) => String(value));
    return expectedValues.some((value) => actualValues.includes(value));
  }

  isSelected(): boolean {
    return this.selectedKey === this.data.key;
  }

  onResetClick(event: MouseEvent): void {
    event.stopPropagation();
    this.resetClick.emit();
  }

  private executeConfiguredApiAction(): void {
    const apiUrl = String(this.data?.apiUrl || '').trim();
    if (!apiUrl) {
      return;
    }

    const filters = this.data?.includeCurrentFilters === true
      ? this.removeEmptyFilterSelections(this.globalFilterService.getCurrentFilters())
      : undefined;

    this.configService
      .getConfig(apiUrl, this.data, filters, this.data?.method)
      .subscribe({
        next: (response) => this.handleDownloadResponse(response),
        error: (error) => console.error(`Button action failed for ${this.data?.key || apiUrl}`, error),
      });
  }

  /**
   * Keep button API payloads consistent with data widgets: a cleared filter is
   * omitted instead of being sent as an empty requestData key.
   */
  private removeEmptyFilterSelections(filters: any): any {
    if (!filters || typeof filters !== 'object' || Array.isArray(filters)) {
      return filters;
    }

    return {
      ...filters,
      requestParams: this.removeEmptySelections(filters.requestParams),
      queryParams: this.removeEmptySelections(filters.queryParams),
    };
  }

  private removeEmptySelections(params: any): any {
    if (!params || typeof params !== 'object' || Array.isArray(params)) {
      return undefined;
    }

    const cleaned = Object.fromEntries(
      Object.entries(params).filter(([, value]) =>
        value !== null &&
        value !== undefined &&
        value !== '' &&
        (!Array.isArray(value) || value.length > 0),
      ),
    );

    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
  }

  /**
   * Export-style actions return the generated file location instead of the file itself, e.g.
   * { responseList: [{ data: [{ execution_status: 'SUCCESS', file_path: '/app/excel-files/x.csv' }] }] }
   * (ConfigService already unwraps that to the inner data object).
   * When the button config declares `downloadOnResponse`, pull the path out and stream the file down.
   */
  private handleDownloadResponse(response: any): void {
    const config = this.getDownloadConfig();
    if (!config) {
      return;
    }

    const directFileBytes = this.isDirectFileByteResponse(response) ? response : undefined;
    const record = directFileBytes === undefined ? this.unwrapResponseRecord(response) : {};
    const statusKey = config.statusKey || 'execution_status';
    const status = record?.[statusKey];
    const successValue = config.successValue || 'SUCCESS';
    if (status !== undefined && String(status).toUpperCase() !== String(successValue).toUpperCase()) {
      console.error(`Export failed for ${this.data?.key}`, record);
      return;
    }

    const filePath = String(record?.[config.pathKey || 'file_path'] || '').trim();
    const fileBytes = directFileBytes ?? record?.[config.bytesKey || 'file_bytes'];
    const returnedFileName = String(record?.[config.fileNameKey || 'file_name'] || '').trim();
    const fileName = filePath.split(/[\\/]/).pop()
      || returnedFileName
      || config.fileName
      || 'generated-price-file.xlsx';

    // Prefer the generated bytes returned by the export API. No second request is needed.
    if (this.hasFileBytes(fileBytes)) {
      try {
        saveAs(this.toFileBlob(fileBytes, fileName, config.contentType), fileName);
      } catch (error) {
        console.error(`Unable to decode generated file for ${this.data?.key}`, error);
      }
      return;
    }

    if (!filePath) {
      console.error(`No file bytes or file path returned for ${this.data?.key}`, record);
      return;
    }

    // Absolute URL — the server already handed us something the browser can fetch directly.
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(filePath)) {
      this.saveFromUrl(filePath, fileName);
      return;
    }

    // Otherwise the path is server-side only and needs a download endpoint to stream it back.
    const downloadApiUrl = String(config.downloadApiUrl || '').trim();
    if (!downloadApiUrl) {
      console.error(
        `downloadOnResponse.downloadApiUrl is not configured for ${this.data?.key}; cannot download ${filePath}`,
      );
      return;
    }

    const params: Record<string, string> = {
      [config.pathParam || 'filePath']: filePath,
    };
    this.saveFromUrl(this.configService.resolveUrl(downloadApiUrl), fileName, params);
  }

  private getDownloadConfig(): any {
    const config = this.data?.downloadOnResponse;
    if (!config) return null;
    return typeof config === 'object' ? config : {};
  }

  private hasFileBytes(value: unknown): boolean {
    if (typeof value === 'string') return value.trim().length > 0;
    if (value instanceof Blob || value instanceof ArrayBuffer || ArrayBuffer.isView(value)) return true;
    if (Array.isArray(value)) return value.length > 0;
    return !!value && typeof value === 'object'
      && Array.isArray((value as { data?: unknown }).data)
      && (value as { data: unknown[] }).data.length > 0;
  }

  private isDirectFileByteResponse(value: unknown): boolean {
    if (typeof value === 'string') return value.trim().length > 0;
    if (value instanceof Blob || value instanceof ArrayBuffer || ArrayBuffer.isView(value)) return true;
    if (Array.isArray(value)) {
      return value.length > 0 && value.every((item) => Number.isInteger(item));
    }
    return !!value && typeof value === 'object'
      && Array.isArray((value as { data?: unknown }).data)
      && (value as { data: unknown[] }).data.every((item) => Number.isInteger(item));
  }

  /** Accept base64, byte arrays, ArrayBuffers, Blobs, and Node-style Buffer JSON. */
  private toFileBlob(fileBytes: unknown, fileName: string, configuredType?: string): Blob {
    if (fileBytes instanceof Blob) return fileBytes;

    const contentType = String(configuredType || '').trim()
      || this.inferFileContentType(fileName);
    let bytes: BlobPart;

    if (typeof fileBytes === 'string') {
      const trimmed = fileBytes.trim();
      const dataUrlMatch = trimmed.match(/^data:([^;,]+)?;base64,(.*)$/s);
      const base64 = (dataUrlMatch?.[2] ?? trimmed).replace(/\s/g, '');
      const binary = window.atob(base64);
      bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
      return new Blob([bytes], { type: dataUrlMatch?.[1] || contentType });
    }

    if (fileBytes instanceof ArrayBuffer) {
      bytes = fileBytes;
    } else if (ArrayBuffer.isView(fileBytes)) {
      bytes = new Uint8Array(fileBytes.buffer, fileBytes.byteOffset, fileBytes.byteLength);
    } else {
      const rawArray = Array.isArray(fileBytes)
        ? fileBytes
        : (fileBytes as { data?: unknown[] })?.data;
      if (!Array.isArray(rawArray)) {
        throw new Error('Unsupported file byte response format.');
      }
      bytes = Uint8Array.from(rawArray.map((value) => Number(value)));
    }

    return new Blob([bytes], { type: contentType });
  }

  private inferFileContentType(fileName: string): string {
    const normalized = fileName.toLowerCase();
    if (normalized.endsWith('.xlsx')) {
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }
    if (normalized.endsWith('.xls')) return 'application/vnd.ms-excel';
    if (normalized.endsWith('.csv')) return 'text/csv;charset=utf-8';
    return 'application/octet-stream';
  }

  private unwrapResponseRecord(response: any): any {
    const directRows = [response?.tableRows, response?.rows];
    for (const rows of directRows) {
      if (Array.isArray(rows) && rows.length > 0) {
        return rows[0]?.data && typeof rows[0].data === 'object'
          ? rows[0].data
          : rows[0];
      }
    }

    if (Array.isArray(response?.responseList) && response.responseList.length > 0) {
      const first = response.responseList[0];
      if (Array.isArray(first?.data) && first.data.length > 0) {
        return first.data[0];
      }
      // Platform entity responses use responseList[0].<entity>[0].
      const entityRows = Object.values(first || {}).find((value) => Array.isArray(value)) as any[];
      if (entityRows?.length > 0) {
        return entityRows[0]?.data && typeof entityRows[0].data === 'object'
          ? entityRows[0].data
          : entityRows[0];
      }
    }
    if (Array.isArray(response) && response.length > 0) {
      return response[0];
    }
    return response;
  }

  private saveFromUrl(url: string, fileName: string, params?: Record<string, string>): void {
    this.http.get(url, { params, responseType: 'blob' }).subscribe({
      next: (blob: Blob) => saveAs(blob, fileName),
      error: (error) => console.error(`Download failed for ${fileName}`, error),
    });
  }

  private buildTooltipText(response: any): string {
    const insights = response?.insights;
    if (!Array.isArray(insights) || insights.length === 0) {
      return 'Insights';
    }

    const formattedText = insights
      .map((item: any) => {
        const period = item?.period || '';
        const points = Array.isArray(item?.points)
          ? item.points.map((point: string) => `• ${point}`).join('\n')
          : '';
        return [period, points].filter(Boolean).join('\n');
      })
      .join('\n\n');

    return formattedText || 'Insights';
  }
}
